/**
 * <TutorLiveActivityTabs> — les vues de l'activité ouverte au centre du live du masteur, sur la seconde ligne du
 * centre : l'énoncé, le rendu, le quiz, le tableau blanc, et le côte à côte au bout (molecule).
 *
 * Wajih, 05/11 : le centre tient sur deux lignes, jamais fusionnées. La première porte l'élève, ses onglets et
 * l'onglet de l'activité (`<TutorLiveActivityTab>` : son titre, son chrono, son statut, sa croix) ; celle-ci, ses
 * vues seules, sous elle. Le lecteur d'écran garde le titre dans le nom de la rangée.
 *
 * Contrôlée : la vue ouverte et le côte à côte arrivent en props.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { type TabItem, Tabs } from "../Tabs"
import { LIVE_CHOSEN_FACE } from "../TutorLiveControlBar"

export type TutorLiveActivityTabsProps<K extends string> = {
    /** Le titre de l'activité ouverte : le nom de la rangée, pour le lecteur d'écran. */
    title: string
    items: TabItem<K>[]
    /** La vue ouverte ; une valeur hors de ces vues (un onglet de l'élève) ne souligne rien. */
    value: K
    onChange: (key: K) => void
    /** « Côte à côte », au bout de la rangée, quand le centre en a la place. Absent : pas de bascule. */
    split?: { on: boolean; onToggle: () => void }
    testID?: string
}

const TEXTS = {
    tabs: (title: string) => `Les vues de l'activité ${title}`,
    split: "Côte à côte",
}

export function TutorLiveActivityTabs<K extends string>(props: TutorLiveActivityTabsProps<K>) {
    const { title, items, value, onChange, split, testID } = props
    return (
        <View style={styles.root} testID={testID}>
            <Tabs
                items={items}
                value={value}
                onChange={onChange}
                size="sm"
                accessibilityLabel={TEXTS.tabs(title)}
                trailing={
                    split != null ? (
                        <IconButton
                            iconName="columns"
                            shape="square"
                            size={32}
                            iconSize={18}
                            {...(split.on ? LIVE_CHOSEN_FACE : { appearance: "transparent", iconColor: "text-strong" })}
                            selected={split.on}
                            onPress={split.onToggle}
                            accessibilityLabel={TEXTS.split}
                        />
                    ) : undefined
                }
            />
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    root: { minWidth: 0 },
}))
